import { useEffect, useMemo, useState } from "react";
import intl from "react-intl-universal";
import { Redirect, useLocation } from "react-router-dom";
import { TbPlayerPlay } from "react-icons/tb";
import { useBaasInstance } from "../../../../Contexts/BaasInstanceContext";
import { useBaasCapabilities } from "../../../../Hooks/useBaasCapabilities";
import { useBaasPageLoading } from "../../../../Hooks/useBaasPageLoading";
import { useToast } from "../../../../Hooks/toast";
import { BaasTablePageSkeleton } from "../components/BaasSkeleton";
import {
  GRID_HISTORY_COLS_4,
  GRID_HISTORY_COLS_5,
  GRID_HISTORY_HEADER_VALUE_CLASS,
  GridHistoryCell,
  GridHistoryContainer,
  GridHistoryHeader,
  GridHistoryRow,
  GridHistoryStatusActionsCell,
  GridHistoryStatusActionsHeader,
  GridPagination,
} from "../../../../Components/Molecules/GridHistory";
import { isBaasHmlInstance } from "../../../../Services/baasInstancesApi";
import {
  buildBaasTransactionsCsv,
  fetchBaasTransactions,
} from "../../../../Services/baasTransactionsApi";
import { formatCurrency } from "../../../../Utils/formatters";
import { paginateList } from "../../Extract/extractFilters";
import { hasBaasBankingRails } from "../baasCapabilities";
import { BaasInstanceNameCell } from "../components/BaasInstanceNameCell";
import {
  BAAS_LIST_FILTER_ALL,
  canUseBaasGlobalInstanceFilter,
  resolveBaasListInstanceNumericId,
  rowMatchesBaasListInstanceFilter,
  stampBaasRowInstance,
} from "../baasInstanceListFilter";
import {
  createDefaultBaasTransactionsFilters,
  filterBaasTransactions,
} from "./baasTransactionsFilters";
import { BaasTransactionsFilterBar } from "./components/BaasTransactionsFilterBar";
import { BaasTransactionsQuickActions } from "./components/BaasTransactionsQuickActions";
import { BaasTransactionDetailDrawer } from "./components/BaasTransactionDetailDrawer";
import { BaasTransactionRefundModal } from "./components/BaasTransactionRefundModal";
import { BaasTransactionSimulateModal } from "./components/BaasTransactionSimulateModal";
import { BaasTransactionIcon } from "./components/BaasTransactionIcon";
import { BaasTransactionStatusBadge } from "./components/BaasTransactionStatusBadge";
import { BaasTransactionsTotalsCards } from "./components/BaasTransactionsTotalsCards";

/** `?accountId=` from "Ver transações" on Contas / Root shell detail. */
const readAccountIdQuery = (search) => {
  const value = new URLSearchParams(search || "").get("accountId");
  return value != null && String(value).trim() !== "" ? String(value).trim() : null;
};

/** Transação (badge + descrição) | Data | Valor | Status + Detalhes — Carteira GridHistory pattern */
const formatDate = (value) => {
  if (!value) return "—";
  return new Intl.DateTimeFormat(undefined, { dateStyle: "short", timeStyle: "short" }).format(
    new Date(value),
  );
};

const TransactionsHeaderRow = ({ showInstanceColumn }) => (
  <GridHistoryHeader colSpan={showInstanceColumn ? 5 : 4}>
    <span>{intl.get("baas.transactions.columns.transaction")}</span>
    {showInstanceColumn ? (
      <span>{intl.get("baas.transactions.columns.instance")}</span>
    ) : null}
    <span>{intl.get("baas.transactions.columns.date")}</span>
    <span className={GRID_HISTORY_HEADER_VALUE_CLASS}>
      {intl.get("baas.transactions.columns.amount")}
    </span>
    <GridHistoryStatusActionsHeader statusLabel={intl.get("baas.transactions.columns.status")} />
  </GridHistoryHeader>
);

const TransactionRow = ({ transaction, onOpen, showInstanceColumn }) => {
  const handleKeyDown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onOpen(transaction);
    }
  };

  const amountCents = Math.abs(Number(transaction.amount) || 0);
  const signedAmount =
    transaction.direction === "outbound" ? -amountCents : amountCents;
  const formatted = formatCurrency(amountCents, transaction.currency);
  const amountLabel =
    signedAmount > 0 ? `+${formatted}` : signedAmount < 0 ? `-${formatted}` : formatted;

  const title =
    transaction.description ||
    transaction.counterpartyName ||
    transaction.referenceId ||
    "—";
  const entityLabel = transaction.entityName || transaction.clientName || "";
  const subtitleParts = [entityLabel].filter(Boolean);

  return (
    <GridHistoryRow
      colSpan={showInstanceColumn ? 5 : 4}
      className="cursor-pointer hover:bg-gray-50/60 dark:hover:bg-brand-dark-elevated/30"
      role="button"
      tabIndex={0}
      onClick={() => onOpen(transaction)}
      onKeyDown={handleKeyDown}
    >
      <GridHistoryCell label={intl.get("baas.transactions.columns.transaction")}>
        <div className="flex min-w-0 items-center gap-3">
          <BaasTransactionIcon transaction={transaction} />
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-brand-dark dark:text-gray-100">
              {title}
            </p>
            {subtitleParts.length ? (
              <p className="truncate text-xs text-gray-500 dark:text-gray-400">
                {subtitleParts.join(" · ")}
              </p>
            ) : null}
          </div>
        </div>
      </GridHistoryCell>
      {showInstanceColumn ? (
        <GridHistoryCell label={intl.get("baas.transactions.columns.instance")}>
          <BaasInstanceNameCell name={transaction.instanceName} />
        </GridHistoryCell>
      ) : null}
      <GridHistoryCell label={intl.get("baas.transactions.columns.date")}>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {formatDate(transaction.createdAt)}
        </p>
      </GridHistoryCell>
      <GridHistoryCell label={intl.get("baas.transactions.columns.amount")} align="right">
        <p
          className={`text-sm font-semibold tabular-nums ${
            signedAmount > 0
              ? "text-emerald-600 dark:text-emerald-400"
              : signedAmount < 0
                ? "text-red-600 dark:text-red-400"
                : "text-brand-dark dark:text-gray-100"
          }`}
        >
          {amountLabel}
        </p>
      </GridHistoryCell>
      <GridHistoryStatusActionsCell
        statusLabel={intl.get("baas.transactions.columns.status")}
        statusBadge={
          <BaasTransactionStatusBadge
            status={transaction.status}
            refunded={transaction.refunded}
            returned={transaction.returned}
          />
        }
        onShowDetail={(item) => {
          onOpen(item);
        }}
        item={transaction}
      />
    </GridHistoryRow>
  );
};

export const BaasTransactionsPage = () => {
  const location = useLocation();
  const { selectedInstance, instances, dataEpoch, listInstanceFilter } = useBaasInstance();
  const { addToast } = useToast();
  const { can, capabilities, loading: capabilitiesLoading } = useBaasCapabilities();
  const canSimulate = can("accounts.transfer");
  const isLoading = useBaasPageLoading(
    selectedInstance?.id ? `${selectedInstance.id}:${dataEpoch}` : null,
  );
  const isHml = isBaasHmlInstance(selectedInstance);
  const showInstanceColumn = canUseBaasGlobalInstanceFilter(selectedInstance);
  const accountIdFilter = useMemo(
    () => readAccountIdQuery(location.search),
    [location.search],
  );
  const [filters, setFilters] = useState(createDefaultBaasTransactionsFilters);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [refreshKey, setRefreshKey] = useState(0);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [refundTransaction, setRefundTransaction] = useState(null);
  const [simulateOpen, setSimulateOpen] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [transactionsLoading, setTransactionsLoading] = useState(false);

  const dateFromKey = filters.dateFrom
    ? filters.dateFrom.getTime?.() ?? String(filters.dateFrom)
    : "";
  const dateToKey = filters.dateTo
    ? filters.dateTo.getTime?.() ?? String(filters.dateTo)
    : "";

  useEffect(() => {
    let cancelled = false;

    if (!selectedInstance) {
      setTransactions([]);
      return undefined;
    }

    setTransactionsLoading(true);
    (async () => {
      try {
        const next = await fetchBaasTransactions(selectedInstance, {
          startDate: filters.dateFrom || undefined,
          endDate: filters.dateTo || undefined,
          fetchAll: true,
          ...(accountIdFilter ? { accountIds: [accountIdFilter] } : {}),
          instanceId: resolveBaasListInstanceNumericId(
            listInstanceFilter,
            selectedInstance,
            instances,
          ),
        });
        if (!cancelled) {
          setTransactions(
            next.map((row) => stampBaasRowInstance(row, selectedInstance)),
          );
        }
      } catch {
        if (!cancelled) setTransactions([]);
      } finally {
        if (!cancelled) setTransactionsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // Re-fetch when instance, account scope, refresh, or date range changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- dateFromKey/dateToKey track filter dates
  }, [
    selectedInstance,
    refreshKey,
    dateFromKey,
    dateToKey,
    dataEpoch,
    listInstanceFilter,
    instances,
    accountIdFilter,
  ]);

  const instanceScopedItems = useMemo(
    () =>
      transactions.filter((row) => {
        if (
          !rowMatchesBaasListInstanceFilter(
            row,
            listInstanceFilter,
            selectedInstance,
            instances,
          )
        ) {
          return false;
        }
        if (!accountIdFilter) return true;
        const rowAccountId = String(row?.accountId || row?.entityId || "").trim();
        return rowAccountId === accountIdFilter;
      }),
    [transactions, listInstanceFilter, selectedInstance, instances, accountIdFilter],
  );

  const filteredItems = useMemo(
    () => filterBaasTransactions(instanceScopedItems, filters),
    [instanceScopedItems, filters],
  );

  const pendingPixItems = useMemo(
    () => instanceScopedItems.filter((row) => Boolean(row?.pendingPix)),
    [instanceScopedItems],
  );

  const paginatedItems = useMemo(
    () => paginateList(filteredItems, page, rowsPerPage),
    [filteredItems, page, rowsPerPage],
  );

  const handleFiltersChange = (nextFilters) => {
    setFilters(nextFilters);
    setPage(1);
  };

  const handleTransactionUpdate = () => {
    setRefreshKey((current) => current + 1);
  };

  const handleRefresh = () => {
    setRefreshKey((current) => current + 1);
    addToast({
      type: "success",
      title: intl.get("baas.transactions.toast.refreshed"),
    });
  };

  const handleExport = () => {
    const csv = buildBaasTransactionsCsv(filteredItems);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `baas-transactions-${selectedInstance?.id || "all"}-${Date.now()}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    addToast({
      type: "success",
      title: intl.get("baas.transactions.toast.exported"),
    });
  };

  const selectedDetail = useMemo(() => {
    if (!selectedTransaction) return null;
    return transactions.find((item) => item.id === selectedTransaction.id) ?? selectedTransaction;
  }, [selectedTransaction, transactions]);

  if (isLoading || capabilitiesLoading || transactionsLoading) {
    return <BaasTablePageSkeleton withQuickActions columns={showInstanceColumn ? 5 : 4} />;
  }

  if (!hasBaasBankingRails(capabilities)) {
    return <Redirect to="/baas/inicio" />;
  }

  return (
    <div className="baas-transactions min-w-0 space-y-5">
      <header className="min-w-0 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-brand-dark dark:text-gray-100">
            {intl.get("baas.nav.transactions")}
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {intl.get(
              showInstanceColumn && listInstanceFilter === BAAS_LIST_FILTER_ALL
                ? "baas.transactions.subtitleMatrix"
                : "baas.transactions.subtitle",
            )}
            {accountIdFilter ? (
              <span className="mt-0.5 block font-mono text-xs text-gray-400 dark:text-gray-500">
                {accountIdFilter}
              </span>
            ) : null}
          </p>
        </div>

        {isHml && canSimulate ? (
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className="btn-primary shrink-0"
              onClick={() => setSimulateOpen(true)}
            >
              <TbPlayerPlay className="h-4 w-4" aria-hidden />
              {intl.get("baas.transactions.simulate.button")}
            </button>
          </div>
        ) : null}
      </header>

      <BaasTransactionsTotalsCards
        items={filteredItems}
        pendingItems={pendingPixItems}
        showPendingCard={showInstanceColumn}
      />

      <BaasTransactionsQuickActions
        isHml={isHml && canSimulate}
        onSimulate={() => setSimulateOpen(true)}
        onExport={handleExport}
        onRefresh={handleRefresh}
      />

      <BaasTransactionsFilterBar
        filters={filters}
        onFiltersChange={handleFiltersChange}
        filteredItems={filteredItems}
        transactions={transactions}
        instances={instances}
        selectedInstance={selectedInstance}
        showInstanceFilter={false}
        showPendingFilter={showInstanceColumn}
      />

      <div className="app-card overflow-hidden">
        {paginatedItems.length === 0 ? (
          <div className="app-empty-state m-4">{intl.get("baas.transactions.empty")}</div>
        ) : (
          <GridHistoryContainer
            gridCols={showInstanceColumn ? GRID_HISTORY_COLS_5 : GRID_HISTORY_COLS_4}
          >
            <TransactionsHeaderRow showInstanceColumn={showInstanceColumn} />
            {paginatedItems.map((transaction) => (
              <TransactionRow
                key={transaction.id}
                transaction={transaction}
                onOpen={setSelectedTransaction}
                showInstanceColumn={showInstanceColumn}
              />
            ))}
          </GridHistoryContainer>
        )}

        <GridPagination
          total={filteredItems.length}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={setPage}
          onRowsPerPageChange={(value) => {
            setRowsPerPage(value);
            setPage(1);
          }}
          currentPageCount={paginatedItems.length}
          itemLabel={intl.get("baas.transactions.paginationLabel")}
        />
      </div>

      <BaasTransactionDetailDrawer
        transaction={selectedDetail}
        onClose={() => setSelectedTransaction(null)}
        onRefundRequest={(txn) => {
          setSelectedTransaction(null);
          setRefundTransaction(txn);
        }}
        onRefundSuccess={handleTransactionUpdate}
      />

      <BaasTransactionRefundModal
        open={Boolean(refundTransaction)}
        transaction={refundTransaction}
        onClose={() => setRefundTransaction(null)}
        onSuccess={() => {
          setRefundTransaction(null);
          handleTransactionUpdate();
        }}
      />

      {isHml ? (
        <BaasTransactionSimulateModal
          open={simulateOpen}
          onClose={() => setSimulateOpen(false)}
          onSuccess={() => {
            handleTransactionUpdate();
            setPage(1);
          }}
        />
      ) : null}
    </div>
  );
};

export default BaasTransactionsPage;
